import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';

/**
 * Gate for role-restricted authenticated routes. Unauthenticated -> /login
 * (preserving `state.from` so ProtectedRoute/RoleRoute's login redirect can
 * send the user back). Authenticated but not one of `roles` -> /403 (blocked,
 * not merely hidden — the server-side role gate on the page's own data
 * endpoints is what actually enforces this; this route guard keeps a wrong-role
 * user from seeing the page shell at all).
 *
 *   <Route element={<RoleRoute roles={['Delivery / Project Lead']} />}>
 *     <Route path="/active-project-delivery-hub" element={<ActiveProjectDeliveryHub />} />
 *   </Route>
 */
export default function RoleRoute({ roles }) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (Array.isArray(roles) && roles.length > 0 && !roles.includes(user?.role)) {
    return <Navigate to="/403" replace />;
  }

  return <Outlet />;
}
